import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import TipBox from '../../components/TipBox.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const STATUS_CYCLE  = { 'not-started': 'drafting', drafting: 'done', done: 'not-started' }
const STATUS_LABELS = { 'not-started': 'Not started', drafting: 'Drafting', done: 'Done' }

export default function ChapterPlanner({ pid = 'w' }) {
  const [chapters, setChapters] = useState([])
  const [confirmId, setConfirmId] = useState(null)
  useEffect(() => { api.getChapters(pid).then(d => setChapters(d || [])) }, [pid])

  function updateChapters(next) {
    setChapters(next)
    api.saveChapters(pid, next)
  }

  function addChapter() {
    updateChapters([...chapters, { id: Date.now(), title: '', notes: '', status: 'not-started', text: '' }])
  }

  function updateChapter(id, field, value) {
    updateChapters(chapters.map(c => c.id === id ? { ...c, [field]: value } : c))
  }

  function cycleStatus(id) {
    updateChapters(chapters.map(c => c.id === id ? { ...c, status: STATUS_CYCLE[c.status] } : c))
  }

  function deleteChapter(id) {
    setConfirmId(id)
  }

  function confirmDelete() {
    updateChapters(chapters.filter(c => c.id !== confirmId))
    setConfirmId(null)
  }

  const done       = chapters.filter(c => c.status === 'done').length
  const drafting   = chapters.filter(c => c.status === 'drafting').length
  const summary    = chapters.length
    ? `${chapters.length} chapter${chapters.length !== 1 ? 's' : ''} · ${done} done · ${drafting} in progress`
    : ''

  return (
    <Card label="Section 7" title="Chapter planner">
      <TipBox title="💡 Planning your chapters" collapsible>
        <p>You don't need to know every scene before you start — but having a loose plan of what each chapter needs to do gives you a compass. Think of each chapter as having a job: it must either <strong>advance plot</strong>, <strong>deepen character</strong>, or <strong>build tension</strong>. Ideally all three.</p>
        <ul>
          <li>End chapters with a micro-tension — a question, a complication, a revelation — to pull readers forward</li>
          <li>Vary pace — alternate between slower, intimate scenes and faster, propulsive ones</li>
          <li>Track whose POV carries each chapter if you're writing multiple perspectives</li>
        </ul>
      </TipBox>

      <div className="chapter-list" style={{ marginTop: '1.25rem' }}>
        {chapters.map((ch, i) => (
          <div key={ch.id} className="chapter-row">
            <span className="chapter-num">Ch.{i + 1}</span>
            <div style={{ flex: 1 }}>
              <input
                className="chapter-title-input"
                value={ch.title}
                placeholder="Chapter title..."
                onChange={e => updateChapter(ch.id, 'title', e.target.value)}
              />
              <input
                className="chapter-notes-input"
                value={ch.notes}
                placeholder="What does this chapter need to do?"
                onChange={e => updateChapter(ch.id, 'notes', e.target.value)}
              />
            </div>
            <button
              className={`status-chip ${ch.status}`}
              onClick={() => cycleStatus(ch.id)}
            >
              {STATUS_LABELS[ch.status]}
            </button>
            <button className="chapter-del" onClick={() => deleteChapter(ch.id)}>✕</button>
          </div>
        ))}
      </div>

      <button className="ghost-btn" onClick={addChapter}>+ Add chapter</button>

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove this chapter?"
        message="This cannot be undone."
        confirmLabel="Remove"
        onConfirm={confirmDelete}
        onCancel={() => setConfirmId(null)}
      />

      {summary && (
        <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--stone)' }}>{summary}</p>
      )}

      <TipBox title="Planning your chapters" collapsible>
        <ul>
          <li>Every chapter needs a job. Write in one sentence what each chapter must accomplish before you write it.</li>
          <li>End chapters on a question, not an answer — readers turn pages to find out what happens next.</li>
          <li>Alternate between high-tension and quieter, more intimate scenes. Unrelenting tension numbs the reader.</li>
          <li>If a chapter could be removed and nothing would change, it probably should be removed.</li>
        </ul>
      </TipBox>
    </Card>
  )
}
